// =============================================================================
// screens/ChannelScreen.tsx — Server channel chat
//
// MIGRATION (bare RN, P3 COMMUNITIES group): ported off apps/cryb-native
// app/channel/[id].tsx. Swaps: expo-router → react-navigation
// (useLocalSearchParams → route.params; inline <Stack.Screen options> →
// navigation.setOptions); @expo/vector-icons → Icon shim; expo-image-picker →
// react-native-image-picker (launchImageLibrary). The realtime CHANNEL CHAT
// correctness — channel-room join/leave, dual-emit dedupe (insertIncoming),
// reconnect rejoin + resyncSince gap-fill, optimistic send/retry, typing — is
// PRESERVED VERBATIM (it's the hardened LR1 code). A15 (Trust & Safety):
// long-press another member's message → ReportBlockSheet; blocked senders
// filtered.
//
// Message history (with load-older pagination), reliable optimistic send +
// failed-send retry, live realtime delivery, typing indicators, reply-to and
// image/file attachments, date separators, and resilient empty/error/offline
// states.
//   GET  /channels/:id/messages?before=<ISO> → { data: { messages:[…desc], pagination } }
//   POST /messages { channelId, content, replyToId?, attachments? } → { data: <completeMessage> }
//   socket: channel:join → messageCreate / message:create (new msg) + channel:typing(_start|_stop)
// =============================================================================
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  View, Text, StyleSheet, FlatList, TextInput, TouchableOpacity, Pressable,
  ActivityIndicator, KeyboardAvoidingView, Platform, Alert, Linking, useWindowDimensions,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { launchImageLibrary } from 'react-native-image-picker';
import Video from 'react-native-video';
import { recorder } from '../services/audio';
import AudioMessageBubble from '../components/chat/AudioMessageBubble';
import { pick, keepLocalCopy, types as docTypes, isErrorWithCode, errorCodes } from '@react-native-documents/picker';
import { Ionicons } from '../components/ui/Icon';
import type { RootStackParamList } from '../navigation/types';
import {
  colors, spacing, radius, fonts, getLayout, layout as layoutTokens,
  typographyScale, shadowTokens,
} from '../constants/theme';
import ParticleBackground from '../components/ui/ParticleBackground';
import Asset3D from '../components/ui/Asset3D';
import { GlassSurface, GlassButton, glassTokens } from '../components/glass';
import { api, getMe, uploadAttachment, uploadMedia } from '../services/api';
import {
  connectSocket,
  joinChannel,
  leaveChannel,
  emitChannelTyping,
  onChannelMessage,
  onChannelTyping,
  onReconnect,
  resyncSince,
  type ChannelMessagePayload,
  type ChannelTypingPayload,
} from '../services/socket';
import ReportBlockSheet, { ReportTarget } from '../components/moderation/ReportBlockSheet';
import { ChannelInfoSheet } from '../components/community';
import { isBlocked, onBlockedChange, isMuted, onMutedChange } from '../services/moderation';

type Nav = NativeStackNavigationProp<RootStackParamList, 'Channel'>;
type ChannelRoute = RouteProp<RootStackParamList, 'Channel'>;

// Text/edge palette for this screen. FIX: this used a DARK (near-white) palette
// but the ParticleBackground here is theme="light" (light lavender) — so the
// text rendered near-white on a light bg = illegible. CRYB is light-mode always;
// these now map to the light-ink roles (matching DMScreen's chat chrome).
const DARK = {
  ink: colors.ink,                      // primary text (near-black)
  ink2: colors.ink2,                    // secondary text
  muted: colors.muted,                  // captions / icons
  faint: colors.faint,                  // empty-state icon
  bubbleOther: 'rgba(255,255,255,0.60)',// other person's bubble = white glass
  bubbleBorder: colors.cardBorder,
  inputBg: 'rgba(255,255,255,0.60)',
  divider: colors.cardBorder,
} as const;

interface Attachment {
  id: string;
  url: string;
  contentType: string;
  filename: string;
  width?: number;
  height?: number;
  duration?: number; // voice-note length (seconds)
}

interface ReplyTo {
  id: string;
  content: string;
  senderName: string;
}

interface ChatMessage {
  id: string;
  content: string;
  senderName: string;
  senderId: string;
  avatar?: string;
  createdAt?: string;
  attachments: Attachment[];
  replyTo?: ReplyTo | null;
  // Correlation id echoed by the server (REST + socket) so a sender can match its
  // own optimistic bubble EXACTLY — not by content, which collides on duplicate text.
  clientMessageId?: string;
  // Optimistic-send lifecycle (local-only).
  pending?: boolean;   // POST in flight
  failed?: boolean;    // send failed — show retry
  uploading?: boolean; // attachment still uploading
}

// Normalize a message from ANY source. Three casings reach this: GET history
// (camelCase: user/replyTo/attachments), POST /messages + socket (PascalCase:
// User/MessageAttachment/Message). Be defensive about both + nulls.
function normalize(m: any): ChatMessage {
  const u = m?.user ?? m?.User ?? {};
  const rawAtt = Array.isArray(m?.attachments)
    ? m.attachments
    : Array.isArray(m?.MessageAttachment)
      ? m.MessageAttachment
      : [];
  const attachments: Attachment[] = rawAtt
    .filter((a: any) => a && (a.url || a.proxyUrl))
    .map((a: any, i: number) => ({
      id: a?.id ?? `att-${i}`,
      url: a?.proxyUrl || a?.url || '',
      contentType: a?.contentType || '',
      filename: a?.filename || 'file',
      width: typeof a?.width === 'number' ? a.width : undefined,
      height: typeof a?.height === 'number' ? a.height : undefined,
    }));
  const r = m?.replyTo ?? (m?.Message ? { ...m.Message, user: m.Message?.User } : null);
  const replyTo: ReplyTo | null = r
    ? {
        id: r?.id ?? '',
        content: typeof r?.content === 'string' ? r.content : '',
        senderName: r?.user?.displayName || r?.user?.username || r?.User?.displayName || r?.User?.username || 'User',
      }
    : null;
  return {
    // Stable fallback (was Math.random() → new key every normalize → remount).
    id: m?.id ?? `${m?.userId ?? u.id ?? 'm'}-${m?.createdAt ?? m?.timestamp ?? ''}`,
    content: typeof m?.content === 'string' ? m.content : '',
    senderName: u.displayName || u.username || 'User',
    senderId: m?.userId ?? u.id ?? '',
    avatar: u.avatar ?? undefined,
    createdAt: m?.createdAt ?? m?.timestamp,
    attachments,
    replyTo,
    clientMessageId: typeof m?.clientMessageId === 'string' ? m.clientMessageId : undefined,
  };
}

// "3:07 PM" wall-clock for a bubble; '' for missing/bad dates.
function formatTime(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  return `${h % 12 || 12}:${m} ${h >= 12 ? 'PM' : 'AM'}`;
}

// "Today" / "Yesterday" / "Mon, Jun 3" date label for separators.
function formatDay(dateStr?: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  const same = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (same(d, today)) return 'Today';
  if (same(d, yest)) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

// True if two messages fall on different calendar days (drives separators).
function differentDay(a?: string, b?: string): boolean {
  if (!a || !b) return false;
  const da = new Date(a), db = new Date(b);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false;
  return da.toDateString() !== db.toDateString();
}

export default function ChannelScreen() {
  const navigation = useNavigation<Nav>();
  const route = useRoute<ChannelRoute>();
  const { id, name } = route.params ?? ({} as { id: string; name?: string });
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: 720, alignSelf: 'center' as const, width: '100%' as const } : undefined;

  const [messages, setMessages] = useState<ChatMessage[]>([]); // newest-first (inverted list)
  const [loading, setLoading] = useState(true);
  // Structural (A7): distinguish a real load failure / non-member 403 from a legit
  // empty channel so the screen never shows a misleading blank "No messages yet".
  const [loadError, setLoadError] = useState<'none' | 'forbidden' | 'offline'>('none');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  // --- Voice-note recording state (services/audio recorder), parity with DMs ---
  const [isRecording, setIsRecording] = useState(false);
  const [recordMillis, setRecordMillis] = useState(0);
  const recordSecs = Math.floor(recordMillis / 1000);
  const recordBusyRef = useRef(false);
  // Single-playback coordination: only one audio bubble plays at a time.
  const [activeAudioId, setActiveAudioId] = useState<string | null>(null);
  const [meId, setMeId] = useState<string>('');
  const meIdRef = useRef<string>('');
  // Reconnect-resync cursor: the largest server timestamp we've rendered. After a
  // socket gap that connectionStateRecovery could NOT replay (recovered===false),
  // we REST-resync everything newer than this so no channel message is silently
  // dropped during the reconnect window (LR1 contract). null until first message.
  const lastSeenTsRef = useRef<string | null>(null);
  // Reply-to compose target (null = not replying).
  const [replyTarget, setReplyTarget] = useState<ChatMessage | null>(null);
  // Pagination (load-older).
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Typing: ids of other members currently typing (with auto-expiry timers).
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const typingTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const lastTypingSentRef = useRef(0);
  // Trust & Safety (Apple 1.2): report/block sheet target + re-render on block.
  const [tsTarget, setTsTarget] = useState<ReportTarget | null>(null);
  const [, setBlockTick] = useState(0);
  useEffect(() => {
    const bump = () => setBlockTick((n) => n + 1);
    const unsubBlocked = onBlockedChange(bump);
    const unsubMuted = onMutedChange(bump);
    return () => { unsubBlocked(); unsubMuted(); };
  }, []);

  const title = (name ? `# ${name}` : 'Channel');
  // Channel info bottom sheet (reference IMG_5872) — opened from the header title.
  const [infoOpen, setInfoOpen] = useState(false);

  // Header (skill §12): hide the opaque native react-navigation bar and render an
  // in-screen translucent GlassSurface header instead, so the message list scrolls
  // UNDER it — no white top strip framing the chat (matches DM/Home/etc). The
  // title stays a tappable "# name ⌄" that opens the channel info sheet; back is
  // chevron-only so the previous route's name never leaks as a label.
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  // In-screen translucent header — back chevron · tappable "# name ⌄" title ·
  // channel-info action. Same handlers as before; only the chrome changed.
  const renderHeader = () => (
    <GlassSurface
      variant="bar"
      mode="light"
      radius={0}
      sheen={false}
      surfaceColor="transparent"
      style={[headerStyles.header, { paddingTop: insets.top + spacing.xs }]}
      contentStyle={headerStyles.headerContent}
    >
      <TouchableOpacity
        style={headerStyles.backBtn}
        onPress={() => navigation.goBack()}
        accessibilityRole="button"
        accessibilityLabel="Back"
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        <Ionicons name="chevron-back" size={26} color={DARK.ink} />
      </TouchableOpacity>
      <TouchableOpacity
        onPress={() => setInfoOpen(true)}
        style={headerStyles.titleWrap}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel={`${title} — channel info`}
      >
        <Text style={headerStyles.titleText} numberOfLines={1}>{title}</Text>
        <Ionicons name="chevron-down" size={14} color={DARK.muted} />
      </TouchableOpacity>
      {/* Balances the back chevron so the title reads visually centered. */}
      <View style={headerStyles.backBtn} />
    </GlassSurface>
  );

  // --- Initial load (history snapshot + me) ---
  const fetchMessages = useCallback(async () => {
    setLoadError('none');
    try {
      const [meRes, msgRes] = await Promise.all([
        api<any>('/auth/me'),
        api<any>(`/channels/${encodeURIComponent(String(id))}/messages?limit=50`),
      ]);
      if (meRes.success) {
        const uid = meRes.data?.user?.id ?? meRes.data?.id ?? '';
        setMeId(uid);
        meIdRef.current = uid;
      }
      if (!msgRes.success) {
        // Membership gate → "Not a member of this server" (403). Surface a clear
        // state instead of a blank empty list.
        const msg = (msgRes.error || '').toLowerCase();
        setLoadError(msg.includes('member') || msg.includes('not a participant') ? 'forbidden' : 'offline');
        setMessages([]);
        return;
      }
      // Endpoint returns newest-first; the inverted list wants newest-first too.
      // Be tolerant of the bare {messages} envelope too (api() unwraps data.data ?? data).
      const raw = Array.isArray(msgRes.data?.messages) ? msgRes.data.messages
        : (Array.isArray(msgRes.data) ? msgRes.data : []);
      setMessages(raw.map(normalize));
      setHasMore(!!msgRes.data?.pagination?.hasMore);
    } catch {
      setLoadError('offline');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // --- Load older (paginate on scroll to top of the inverted list) ---
  const loadOlder = useCallback(async () => {
    if (loadingMore || !hasMore || messages.length === 0) return;
    // The oldest loaded message is the LAST item (list is newest-first).
    const oldest = messages[messages.length - 1];
    const before = oldest?.createdAt;
    if (!before) return;
    setLoadingMore(true);
    try {
      const res = await api<any>(
        `/channels/${encodeURIComponent(String(id))}/messages?limit=50&before=${encodeURIComponent(before)}`,
      );
      if (res.success) {
        const raw = Array.isArray(res.data?.messages) ? res.data.messages
          : (Array.isArray(res.data) ? res.data : []);
        const older = raw.map(normalize);
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const fresh = older.filter((m: ChatMessage) => !seen.has(m.id));
          return [...prev, ...fresh]; // append older to the tail (newest-first list)
        });
        setHasMore(!!res.data?.pagination?.hasMore && raw.length > 0);
      }
    } catch {
      // keep current messages; user can retry by scrolling again
    } finally {
      setLoadingMore(false);
    }
  }, [id, hasMore, loadingMore, messages]);

  // Track the newest server timestamp we've rendered (drives reconnect-resync).
  // Whenever messages change, advance the cursor to the max real (non-temp) ts.
  useEffect(() => {
    let maxTs = lastSeenTsRef.current;
    for (const m of messages) {
      if (m.id.startsWith('temp-') || !m.createdAt) continue;
      if (!maxTs || new Date(m.createdAt).getTime() > new Date(maxTs).getTime()) {
        maxTs = m.createdAt;
      }
    }
    if (maxTs) lastSeenTsRef.current = maxTs;
  }, [messages]);

  // Insert a single incoming/realtime message: dedupe by id, reconcile our own
  // pending optimistic temp-*, else prepend (newest-first list). Shared by the
  // live socket listener AND the reconnect-resync path so both behave identically.
  const insertIncoming = useCallback((raw: any) => {
    const incoming = normalize(raw);
    const mine = !!meIdRef.current && incoming.senderId === meIdRef.current;
    setMessages((prev) => {
      // Already have the real message (REST send echo or duplicate)? skip.
      if (prev.some((m) => m.id === incoming.id)) return prev;
      // Our own message echoed back — reconcile a pending optimistic temp-*.
      // Prefer an exact clientMessageId match (robust for duplicate text); fall
      // back to content only when the echo carries no correlation id.
      if (mine) {
        const cmid = incoming.clientMessageId;
        const tempIdx = prev.findIndex(
          (m) => m.id.startsWith('temp-') && !m.failed && (
            cmid ? m.clientMessageId === cmid : m.content === incoming.content
          ),
        );
        if (tempIdx !== -1) {
          const next = [...prev];
          next[tempIdx] = incoming;
          return next;
        }
      }
      return [incoming, ...prev];
    });
  }, []);

  // --- Realtime: join the channel room + listen for new messages/typing ---
  useEffect(() => {
    if (!id) return;
    const cid = String(id);
    connectSocket();
    joinChannel(cid);

    const unsubMsg = onChannelMessage((payload: ChannelMessagePayload) => {
      if (payload.channelId && payload.channelId !== cid) return;
      insertIncoming(payload.message);
    });

    // Reconnect-resync (LR1): the socket layer re-joins the channel room on every
    // reconnect, but if connectionStateRecovery could NOT replay the gap
    // (recovered===false — background >2min / cold start), messages sent while we
    // were away were never delivered as live events. REST-resync them so the
    // channel can't silently drop messages across a reconnect.
    const unsubReconnect = onReconnect(async (info) => {
      if (info.recovered) return; // session restored — live events already replayed
      const since = lastSeenTsRef.current;
      if (!since) return; // nothing rendered yet; the initial load covers us
      const result = await resyncSince(since, [cid]);
      const missed = result?.channelMessages?.[cid];
      if (Array.isArray(missed) && missed.length > 0) {
        for (const m of missed) insertIncoming(m);
      }
    });

    const unsubTyping = onChannelTyping((p: ChannelTypingPayload) => {
      if (p.channelId && p.channelId !== cid) return;
      if (!p.userId || (meIdRef.current && p.userId === meIdRef.current)) return;
      if (p.typing) {
        setTypingUsers((prev) => (prev.includes(p.userId) ? prev : [...prev, p.userId]));
        if (typingTimers.current[p.userId]) clearTimeout(typingTimers.current[p.userId]);
        typingTimers.current[p.userId] = setTimeout(() => {
          setTypingUsers((prev) => prev.filter((uId) => uId !== p.userId));
          delete typingTimers.current[p.userId];
        }, 6000);
      } else {
        setTypingUsers((prev) => prev.filter((uId) => uId !== p.userId));
        if (typingTimers.current[p.userId]) {
          clearTimeout(typingTimers.current[p.userId]);
          delete typingTimers.current[p.userId];
        }
      }
    });

    return () => {
      unsubMsg();
      unsubTyping();
      unsubReconnect();
      leaveChannel(cid);
      Object.values(typingTimers.current).forEach(clearTimeout);
      typingTimers.current = {};
    };
  }, [id, insertIncoming]);

  // Best-effort current-user resolve (covers the auth/me race for attribution).
  useEffect(() => {
    let active = true;
    getMe()
      .then((res) => {
        const uid = (res as any)?.data?.id ?? (res as any)?.data?.user?.id;
        if (active && uid) { setMeId(uid); meIdRef.current = uid; }
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // --- Typing (send): throttled ping while composing ---
  const TYPING_THROTTLE_MS = 3000;
  const handleChangeText = useCallback((t: string) => {
    setText(t);
    if (t.trim()) {
      const now = Date.now();
      if (now - lastTypingSentRef.current > TYPING_THROTTLE_MS) {
        lastTypingSentRef.current = now;
        emitChannelTyping(String(id));
      }
    }
  }, [id]);

  // --- Send (optimistic + reconcile, or mark failed for retry) ---
  const doSend = useCallback(async (
    body: string,
    replyToId: string | undefined,
    attachments: Array<{ url: string; filename: string; size?: number; contentType: string }> | undefined,
    optimisticId: string,
    fallback: ChatMessage,
  ) => {
    const res = await api<any>('/messages', {
      method: 'POST',
      body: JSON.stringify({
        channelId: id,
        clientMessageId: optimisticId,
        ...(body ? { content: body } : {}),
        ...(replyToId ? { replyToId } : {}),
        ...(attachments && attachments.length ? { attachments } : {}),
      }),
    });
    if (res.success) {
      const created = res.data?.message ?? res.data;
      const saved = created ? normalize(created) : { ...fallback, id: optimisticId, pending: false };
      setMessages((prev) =>
        // Drop the optimistic AND any realtime echo that may have already arrived.
        prev.some((m) => m.id === saved.id && m.id !== optimisticId)
          ? prev.filter((m) => m.id !== optimisticId)
          : prev.map((m) => (m.id === optimisticId ? saved : m)),
      );
    } else {
      // Mark the optimistic bubble failed so the user can tap to retry.
      setMessages((prev) =>
        prev.map((m) => (m.id === optimisticId ? { ...m, pending: false, failed: true } : m)),
      );
    }
  }, [id]);

  async function handleSend() {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setText('');
    const reply = replyTarget;
    setReplyTarget(null);
    // Unique per-send id — `temp-${Date.now()}` collided for two messages sent in
    // the same millisecond → duplicate optimistic keys → one got stuck/not sent.
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimistic: ChatMessage = {
      id: tempId,
      clientMessageId: tempId,
      content: body,
      senderName: 'You',
      senderId: meIdRef.current || meId,
      createdAt: new Date().toISOString(),
      attachments: [],
      replyTo: reply ? { id: reply.id, content: reply.content, senderName: reply.senderName } : null,
      pending: true,
    };
    setMessages((prev) => [optimistic, ...prev]);
    setSending(false);
    await doSend(body, reply?.id, undefined, tempId, optimistic);
  }

  // Retry a previously-failed message in place (text/reply or already-uploaded att).
  const retrySend = useCallback(async (msg: ChatMessage) => {
    if (msg.attachments.length > 0) {
      const atts = msg.attachments
        .filter((a) => a.url && !a.url.startsWith('file:'))
        .map((a) => ({ url: a.url, filename: a.filename, contentType: a.contentType }));
      if (atts.length === 0) {
        Alert.alert('Cannot retry', 'Please pick the file again.');
        return;
      }
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, failed: false, pending: true } : m)));
      await doSend(msg.content, msg.replyTo?.id, atts, msg.id, msg);
      return;
    }
    setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, failed: false, pending: true } : m)));
    await doSend(msg.content, msg.replyTo?.id, undefined, msg.id, msg);
  }, [doSend]);

  // --- Attachment (image/file) send: optimistic local bubble → upload → POST ---
  async function handlePickImage() {
    if (sending) return;
    try {
      // react-native-image-picker handles permissions internally; a denied
      // permission resolves to { didCancel } / { errorCode }, never a throw.
      const result = await launchImageLibrary({ mediaType: 'mixed', quality: 0.8, selectionLimit: 1 });
      if (result.didCancel || result.errorCode || !result.assets?.[0]) {
        if (result.errorCode && result.errorCode !== 'permission') {
          Alert.alert('Error', 'Could not open the photo library.');
        } else if (result.errorCode === 'permission') {
          Alert.alert('Permission needed', 'Photo library access is required to share media.');
        }
        return;
      }
      const asset = result.assets[0];
      const uri = asset.uri || '';
      if (!uri) return;
      const isVideo = (asset.type || '').startsWith('video/');
      const filename = asset.fileName || uri.split('/').pop() || `${isVideo ? 'video' : 'image'}-${Date.now()}.${isVideo ? 'mp4' : 'jpg'}`;
      const reply = replyTarget;
      setReplyTarget(null);
      const tempId = `temp-${Date.now()}`;
      const optimistic: ChatMessage = {
        id: tempId,
        content: '',
        senderName: 'You',
        senderId: meIdRef.current || meId,
        createdAt: new Date().toISOString(),
        attachments: [{
          id: `local-${Date.now()}`,
          url: uri,
          contentType: asset.type || 'image/jpeg',
          filename,
          width: typeof asset.width === 'number' ? asset.width : undefined,
          height: typeof asset.height === 'number' ? asset.height : undefined,
        }],
        replyTo: reply ? { id: reply.id, content: reply.content, senderName: reply.senderName } : null,
        pending: true,
        uploading: true,
      };
      setMessages((prev) => [optimistic, ...prev]);

      // Video → /uploads/media (accepts video/*, returns just the URL, so carry
      // the asset's own mime/size). Image → /uploads/attachment ({url,...}).
      const mediaContentType = asset.type || (isVideo ? 'video/mp4' : 'image/jpeg');
      const mediaSize = typeof asset.fileSize === 'number' ? asset.fileSize : undefined;
      let up: { url: string; filename: string; size?: number; contentType: string } | null = null;
      if (isVideo) {
        const url = await uploadMedia(uri);
        if (url) up = { url, filename, size: mediaSize, contentType: mediaContentType };
      } else {
        const uploaded = await uploadAttachment(uri);
        if (uploaded?.url) up = { url: uploaded.url, filename: uploaded.filename || filename, size: uploaded.size, contentType: uploaded.contentType || 'image/jpeg' };
      }
      if (!up) {
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, uploading: false, pending: false, failed: true } : m)),
        );
        return;
      }
      // Swap the local file uri for the uploaded url; clear uploading.
      const savedAtt: Attachment = {
        id: `att-${Date.now()}`,
        url: up.url,
        contentType: up.contentType,
        filename: up.filename,
        width: optimistic.attachments[0]?.width,
        height: optimistic.attachments[0]?.height,
      };
      const updated: ChatMessage = { ...optimistic, uploading: false, attachments: [savedAtt] };
      setMessages((prev) => prev.map((m) => (m.id === tempId ? updated : m)));
      await doSend('', reply?.id, [{
        url: up.url,
        filename: up.filename,
        size: up.size,
        contentType: up.contentType,
      }], tempId, updated);
    } catch {
      Alert.alert('Error', 'Could not attach the media.');
    }
  }

  // VOICE NOTES: start recording (mic press). services/audio handles the mic
  // permission prompt; a denied/failed start returns false (graceful).
  async function handleStartRecording() {
    if (isRecording || recordBusyRef.current || sending) return;
    try {
      const ok = await recorder.start();
      if (!ok) {
        Alert.alert('Recording error', 'Could not start recording. Microphone access may be required.');
        return;
      }
      setIsRecording(true);
      setRecordMillis(0);
      recorder.subscribe(({ durationMillis }) => setRecordMillis(durationMillis));
    } catch {
      Alert.alert('Recording error', 'Could not start recording.');
    }
  }

  // VOICE NOTES: stop → upload (audio-capable /uploads/media) → optimistic bubble
  // → send. `cancel` discards without sending. Mirrors handlePickImage's flow.
  async function handleStopRecording(cancel = false) {
    if (recordBusyRef.current) return;
    recordBusyRef.current = true;
    const seconds = Math.floor(recordMillis / 1000);
    let uri: string | null = null;
    try { uri = await recorder.stop(); } catch { uri = null; }
    setIsRecording(false);
    setRecordMillis(0);
    recordBusyRef.current = false;

    if (cancel || !uri || seconds < 1) {
      if (!cancel && uri && seconds < 1) Alert.alert('Too short', 'Hold to record a longer voice message.');
      return;
    }

    const filename = uri.split('/').pop() || `voice-${Date.now()}.m4a`;
    const reply = replyTarget;
    setReplyTarget(null);
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimistic: ChatMessage = {
      id: tempId,
      clientMessageId: tempId,
      content: '',
      senderName: 'You',
      senderId: meIdRef.current || meId,
      createdAt: new Date().toISOString(),
      attachments: [{ id: `local-${Date.now()}`, url: uri, contentType: 'audio/m4a', filename, duration: seconds }],
      replyTo: reply ? { id: reply.id, content: reply.content, senderName: reply.senderName } : null,
      pending: true,
      uploading: true,
    };
    setMessages((prev) => [optimistic, ...prev]);

    const url = await uploadMedia(uri);
    if (!url) {
      setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, uploading: false, pending: false, failed: true } : m)));
      return;
    }
    const savedAtt: Attachment = { id: `att-${Date.now()}`, url, contentType: 'audio/m4a', filename, duration: seconds };
    const updated: ChatMessage = { ...optimistic, uploading: false, attachments: [savedAtt] };
    setMessages((prev) => prev.map((m) => (m.id === tempId ? updated : m)));
    await doSend('', reply?.id, [{ url, filename, size: 0, contentType: 'audio/m4a' }], tempId, updated);
  }

  // DOCUMENT: pick a file → copy to a readable path → upload via /uploads/attachment
  // (docs allowlist) → optimistic file chip → send. Mirrors handlePickImage.
  async function handlePickDocument() {
    if (sending) return;
    try {
      const [file] = await pick({
        type: [docTypes.pdf, docTypes.docx, docTypes.doc, docTypes.xlsx, docTypes.xls, docTypes.ppt, docTypes.pptx, docTypes.plainText, docTypes.csv, docTypes.zip],
      });
      if (!file?.uri) return;
      const name = file.name || file.uri.split('/').pop() || `document-${Date.now()}`;
      const ct = file.type || 'application/octet-stream';
      let uploadUri = file.uri;
      try {
        const [copy] = await keepLocalCopy({ files: [{ uri: file.uri, fileName: name }], destination: 'cachesDirectory' });
        if (copy?.status === 'success' && copy.localUri) uploadUri = copy.localUri;
      } catch { /* fall back to the original uri */ }

      const reply = replyTarget;
      setReplyTarget(null);
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const optimistic: ChatMessage = {
        id: tempId,
        clientMessageId: tempId,
        content: '',
        senderName: 'You',
        senderId: meIdRef.current || meId,
        createdAt: new Date().toISOString(),
        attachments: [{ id: `local-${Date.now()}`, url: uploadUri, contentType: ct, filename: name }],
        replyTo: reply ? { id: reply.id, content: reply.content, senderName: reply.senderName } : null,
        pending: true,
        uploading: true,
      };
      setMessages((prev) => [optimistic, ...prev]);

      const uploaded = await uploadAttachment(uploadUri);
      if (!uploaded?.url) {
        setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, uploading: false, pending: false, failed: true } : m)));
        return;
      }
      const savedAtt: Attachment = { id: `att-${Date.now()}`, url: uploaded.url, contentType: uploaded.contentType || ct, filename: uploaded.filename || name };
      const updated: ChatMessage = { ...optimistic, uploading: false, attachments: [savedAtt] };
      setMessages((prev) => prev.map((m) => (m.id === tempId ? updated : m)));
      await doSend('', reply?.id, [{ url: uploaded.url, filename: uploaded.filename || name, size: uploaded.size, contentType: uploaded.contentType || ct }], tempId, updated);
    } catch (e) {
      if (isErrorWithCode(e) && e.code === errorCodes.OPERATION_CANCELED) return;
      Alert.alert('Error', 'Could not attach the document.');
    }
  }

  // Channel info sheet (IMG_5872): description + owner edit, Members (routes to
  // the community interior's Key Info), thread count, Leave Chat. Rendered in
  // every branch so the header-title tap always works (incl. error states).
  const infoSheet = (
    <ChannelInfoSheet
      visible={infoOpen}
      onClose={() => setInfoOpen(false)}
      channelId={String(id || '')}
      channelName={name || 'channel'}
      meId={meId}
      onOpenMembers={(serverId) => navigation.navigate('ServerDetail', { id: serverId, initialTab: 'info' })}
      onLeft={() => navigation.popToTop()}
    />
  );

  // Visible messages (block-filter; A15).
  const visible = useMemo(
    () => (Array.isArray(messages) ? messages : []).filter(
      (m) => (!!meId && m.senderId === meId) || !(isBlocked(m.senderId) || isMuted(m.senderId)),
    ),
    [messages, meId],
  );

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground mesh={false} theme="light" style={StyleSheet.absoluteFill} />
        {renderHeader()}
        <View style={[styles.center, { flex: 1 }]}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
        {infoSheet}
      </View>
    );
  }

  // Structural error states (A7) — a non-member 403 or an offline failure should
  // never render as a blank channel. Composer is hidden; a Retry / Back is shown.
  if (loadError !== 'none') {
    const forbidden = loadError === 'forbidden';
    return (
      <View style={styles.screen}>
        <ParticleBackground mesh={false} theme="light" style={StyleSheet.absoluteFill} />
        {renderHeader()}
        <View style={[styles.center, { flex: 1 }]}>
        <GlassSurface variant="card" mode="light" radius={radius.xl} contentStyle={styles.errCardContent} style={styles.errCard}>
          <Asset3D name={forbidden ? 'shield' : 'error'} size={110} style={{ marginBottom: spacing.sm }} />
          <Text style={styles.errTitle}>{forbidden ? 'Join to view this channel' : "Couldn't load messages"}</Text>
          <Text style={styles.errSub}>{forbidden ? 'You need to be a member of this server to read its channels.' : 'Check your connection and try again.'}</Text>
          <GlassButton
            title={forbidden ? 'Try Again' : 'Retry'}
            icon="refresh-outline"
            onPress={() => { setLoading(true); fetchMessages(); }}
            fullWidth
            style={styles.errCta}
          />
        </GlassSurface>
        </View>
        {infoSheet}
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      {/* Full-bleed living particle field (skill §12) — under the status bar and
          all chrome; every sibling chat screen renders this, Channel now matches. */}
      <ParticleBackground mesh={false} theme="light" style={StyleSheet.absoluteFill} />
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined} keyboardVerticalOffset={90}>
        {/* Translucent in-screen header — the message list scrolls UNDER it. */}
        {renderHeader()}
        <FlatList
          // Only invert when there ARE rows. RN counter-flips each inverted cell,
          // so a scaleY:-1 in content double-flips → mirrored (day pill / empty).
          // Empty state renders in a NON-inverted list, upright. (Matches DMScreen.)
          inverted={visible.length > 0}
          data={visible}
          keyExtractor={(item) => item.id}
          contentContainerStyle={[
            styles.listContent,
            // Inverted list → the visual TOP is the content's paddingBottom. Clear
            // the floating glass header so the first message scrolls under (not
            // behind) it; the bar's own shadow provides separation (skill §12).
            { paddingBottom: insets.top + spacing.xxxl + spacing.md },
            containerStyle,
          ]}
          showsVerticalScrollIndicator={false}
          onEndReached={loadOlder}
          onEndReachedThreshold={0.4}
          // Virtualization: cap how many rows are mounted so a parent re-render
          // (e.g. every composer keystroke) only re-renders the visible window,
          // not the whole thread — kills typing/scroll jank on long channels.
          initialNumToRender={15}
          maxToRenderPerBatch={10}
          windowSize={11}
          removeClippedSubviews
          updateCellsBatchingPeriod={50}
          ListFooterComponent={loadingMore ? (
            <View style={styles.loadMoreRow}><ActivityIndicator size="small" color={colors.primary} /></View>
          ) : null}
          renderItem={({ item, index }) => {
            const mine = !!meId && item.senderId === meId;
            // In an inverted list the NEXT (older) item is at index+1. Show a date
            // separator above the FIRST message of each day.
            const older = visible[index + 1];
            const showDay = !older || differentDay(item.createdAt, older.createdAt);
            // Long-press another member's message → report/block them (Apple 1.2).
            const onLongPress = mine || !item.senderId
              ? undefined
              : () => setTsTarget({
                  type: 'channel_message',
                  authorId: item.senderId,
                  authorLabel: item.senderName || 'this user',
                  text: item.content,
                });
            return (
              <>
                <View style={[styles.msgRow, mine && styles.msgRowMine]}>
                  {!mine && (
                    item.avatar ? (
                      <CachedImage source={{ uri: item.avatar }} style={styles.avatar} />
                    ) : (
                      <View style={[styles.avatar, styles.avatarFallback]}>
                        <Text style={styles.avatarLetter}>{(item.senderName.charAt(0) || 'U').toUpperCase()}</Text>
                      </View>
                    )
                  )}
                  <View style={[styles.bubbleCol, mine && { alignItems: 'flex-end' }]}>
                    <Pressable
                      onLongPress={onLongPress}
                      delayLongPress={350}
                      accessibilityRole={onLongPress ? 'button' : undefined}
                      accessibilityLabel={onLongPress ? 'Message options — report or block sender' : undefined}
                      style={[styles.bubble, mine ? styles.bubbleMine : styles.bubbleOther, item.failed && styles.bubbleFailed]}
                    >
                      {!mine && <Text style={styles.senderName}>{item.senderName}</Text>}
                      {item.replyTo && (
                        <View style={[styles.replyQuote, mine && styles.replyQuoteMine]}>
                          <Text style={[styles.replyName, mine && styles.replyNameMine]} numberOfLines={1}>{item.replyTo.senderName}</Text>
                          <Text style={[styles.replyText, mine && styles.replyTextMine]} numberOfLines={1}>{item.replyTo.content || 'Attachment'}</Text>
                        </View>
                      )}
                      {item.attachments.map((att) => {
                        const ct = att.contentType || '';
                        if (ct.startsWith('image/')) {
                          return (
                            <View key={att.id}>
                              <CachedImage source={{ uri: att.url }} style={styles.imageAtt} resizeMode="cover" />
                              {item.uploading && (
                                <View style={styles.uploadOverlay}><ActivityIndicator size="small" color={colors.white} /></View>
                              )}
                            </View>
                          );
                        }
                        if (ct.startsWith('video/')) {
                          return (
                            <View key={att.id}>
                              <Video source={{ uri: att.url }} style={styles.imageAtt} resizeMode="cover" paused controls repeat={false} />
                              {item.uploading && (
                                <View style={styles.uploadOverlay}><ActivityIndicator size="small" color={colors.white} /></View>
                              )}
                            </View>
                          );
                        }
                        if (ct.startsWith('audio/')) {
                          return (
                            <View key={att.id}>
                              <AudioMessageBubble
                                uri={att.url}
                                durationSeconds={att.duration}
                                isMine={mine}
                                bubbleId={att.id}
                                activeId={activeAudioId}
                                onActivate={setActiveAudioId}
                              />
                              {item.uploading && (
                                <Text style={{ fontSize: 11, color: mine ? 'rgba(255,255,255,0.8)' : DARK.muted, marginTop: 2 }}>Uploading…</Text>
                              )}
                            </View>
                          );
                        }
                        return (
                          <TouchableOpacity
                            key={att.id}
                            style={styles.fileChip}
                            activeOpacity={0.7}
                            disabled={item.uploading || !att.url}
                            onPress={() => att.url && Linking.openURL(att.url).catch(() => Alert.alert('Cannot open', 'This file could not be opened.'))}
                            accessibilityRole="button"
                            accessibilityLabel={`Open ${att.filename || 'attachment'}`}
                          >
                            <Ionicons name="document-outline" size={18} color={mine ? colors.white : colors.primaryLight} />
                            <Text numberOfLines={1} style={[styles.fileChipText, { color: mine ? colors.white : DARK.ink }]}>{att.filename}</Text>
                            {item.uploading && <ActivityIndicator size="small" color={mine ? colors.white : colors.primaryLight} />}
                          </TouchableOpacity>
                        );
                      })}
                      {!!item.content && <Text style={[styles.msgText, mine && styles.msgTextMine]}>{item.content}</Text>}
                      <View style={styles.metaRow}>
                        {item.pending && <ActivityIndicator size="small" color={mine ? 'rgba(255,255,255,0.7)' : DARK.muted} style={{ transform: [{ scale: 0.7 }] }} />}
                        {!!item.createdAt && !item.pending && !item.failed && (
                          <Text style={[styles.timeText, mine && styles.timeTextMine]}>{formatTime(item.createdAt)}</Text>
                        )}
                      </View>
                    </Pressable>
                    {/* Reply action on another member's message. */}
                    {!mine && !!item.senderId && (
                      <TouchableOpacity style={styles.replyAction} onPress={() => setReplyTarget(item)} accessibilityRole="button" accessibilityLabel="Reply to this message">
                        <Ionicons name="arrow-undo-outline" size={13} color={DARK.muted} />
                        <Text style={styles.replyActionText}>Reply</Text>
                      </TouchableOpacity>
                    )}
                    {item.failed && (
                      <TouchableOpacity style={styles.retryInline} onPress={() => retrySend(item)} accessibilityRole="button" accessibilityLabel="Retry sending message">
                        <Ionicons name="alert-circle" size={13} color={colors.error} />
                        <Text style={styles.retryInlineText}>Not delivered · Tap to retry</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
                {showDay && !!item.createdAt && (
                  <View style={styles.daySepWrap}>
                    <GlassSurface
                      variant="pill"
                      radius={glassTokens.radius.pill}
                      sheen={false}
                      surfaceColor="rgba(255,255,255,0.16)"
                      contentStyle={styles.dayPill}
                    >
                      <Text style={styles.dayPillText}>{formatDay(item.createdAt)}</Text>
                    </GlassSurface>
                  </View>
                )}
              </>
            );
          }}
          ListEmptyComponent={
            <View style={styles.empty}>
              <Asset3D name="chat" size={110} style={{ marginBottom: spacing.sm }} />
              <Text style={styles.emptyText}>No messages yet — say hi</Text>
              <Text style={styles.emptySub}>Be the first to start the conversation in {title}.</Text>
            </View>
          }
        />

        {/* Typing indicator above the composer. */}
        {typingUsers.length > 0 && (
          <View style={[styles.typingRow, containerStyle]}>
            <Text style={styles.typingText}>
              {typingUsers.length === 1 ? 'Someone is typing…' : `${typingUsers.length} people are typing…`}
            </Text>
          </View>
        )}

        {/* Reply preview above the composer. */}
        {replyTarget && (
          <View style={[styles.replyBar, containerStyle]}>
            <View style={styles.replyBarBody}>
              <Text style={styles.replyBarName} numberOfLines={1}>Replying to {replyTarget.senderName}</Text>
              <Text style={styles.replyBarText} numberOfLines={1}>{replyTarget.content || 'Attachment'}</Text>
            </View>
            <TouchableOpacity onPress={() => setReplyTarget(null)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} accessibilityRole="button" accessibilityLabel="Cancel reply">
              <Ionicons name="close" size={20} color={DARK.ink2} />
            </TouchableOpacity>
          </View>
        )}

        <GlassSurface variant="bar" mode="light" radius={0} sheen={false} style={[styles.composerWrap, containerStyle]} contentStyle={[styles.composer, { paddingBottom: Math.max(insets.bottom, spacing.md) }]}>
          {isRecording ? (
            <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: spacing.md }}>
              <TouchableOpacity style={styles.iconBtn} onPress={() => handleStopRecording(true)} accessibilityRole="button" accessibilityLabel="Cancel recording">
                <Ionicons name="trash-outline" size={22} color={colors.error} />
              </TouchableOpacity>
              <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                <View style={{ width: 9, height: 9, borderRadius: 5, backgroundColor: colors.error }} />
                <Text style={{ color: DARK.ink, fontWeight: '700', fontVariant: ['tabular-nums'] }}>
                  {`${Math.floor(recordSecs / 60)}:${String(recordSecs % 60).padStart(2, '0')}`}
                </Text>
                <Text style={{ color: DARK.muted }}>Recording…</Text>
              </View>
              <TouchableOpacity style={styles.sendBtn} onPress={() => handleStopRecording(false)} accessibilityRole="button" accessibilityLabel="Send voice message">
                <Ionicons name="send" size={18} color={colors.white} />
              </TouchableOpacity>
            </View>
          ) : (
            <>
              <TouchableOpacity style={styles.iconBtn} onPress={handlePickImage} disabled={sending} accessibilityRole="button" accessibilityLabel="Attach a photo or video">
                <Ionicons name="image-outline" size={24} color={colors.primaryLight} />
              </TouchableOpacity>
              <TouchableOpacity style={styles.iconBtn} onPress={handlePickDocument} disabled={sending} accessibilityRole="button" accessibilityLabel="Attach a document">
                <Ionicons name="document-attach-outline" size={24} color={colors.primaryLight} />
              </TouchableOpacity>
              <TextInput
                style={styles.input}
                placeholder={`Message ${name ? '#' + name : 'channel'}`}
                placeholderTextColor={DARK.muted}
                value={text}
                onChangeText={handleChangeText}
                multiline
                maxLength={4000}
              />
              {text.trim() ? (
                <TouchableOpacity style={[styles.sendBtn, sending && styles.sendBtnDisabled]} onPress={handleSend} disabled={sending} accessibilityRole="button" accessibilityLabel="Send message">
                  <Ionicons name="send" size={18} color={colors.white} />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity style={styles.iconBtn} onPress={handleStartRecording} disabled={sending} accessibilityRole="button" accessibilityLabel="Record a voice message">
                  <Ionicons name="mic-outline" size={24} color={colors.primaryLight} />
                </TouchableOpacity>
              )}
            </>
          )}
        </GlassSurface>
      </KeyboardAvoidingView>
      <ReportBlockSheet
        visible={!!tsTarget}
        target={tsTarget}
        onClose={() => setTsTarget(null)}
        onBlocked={() => setTsTarget(null)}
      />
      {infoSheet}
    </View>
  );
}

// In-screen translucent header (skill §12) — back chevron · tappable "# name ⌄"
// title (opens the channel info sheet) · a spacer that balances the back button.
const headerStyles = StyleSheet.create({
  header: {
    // GlassSurface variant="bar" supplies fill + border + shadow (scroll-edge
    // separation, no hard divider). paddingTop is set inline (insets.top).
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingBottom: spacing.sm,
    gap: spacing.xs,
  },
  backBtn: { width: 44, height: 44, alignItems: 'center', justifyContent: 'center' },
  titleWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  // §15: tighten the large title slightly (negative tracking) for a crisper header.
  titleText: { ...typographyScale.h3, color: DARK.ink, flexShrink: 1, letterSpacing: -0.3 },
});

const styles = StyleSheet.create({
  // Root: transparent so the full-bleed ParticleBackground shows through every
  // layer (header, list, composer are all translucent glass on top).
  screen: { flex: 1 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },
  listContent: { padding: spacing.lg, paddingBottom: layoutTokens.bottomNavInset, flexGrow: 1 },
  loadMoreRow: { paddingVertical: spacing.md, alignItems: 'center' },
  msgRow: { flexDirection: 'row', alignItems: 'flex-end', gap: spacing.sm, marginBottom: spacing.md },
  msgRowMine: { justifyContent: 'flex-end' },
  bubbleCol: { maxWidth: '78%' },
  avatar: { width: 32, height: 32, borderRadius: radius.full },
  avatarFallback: { backgroundColor: 'rgba(108,92,231,0.32)', alignItems: 'center', justifyContent: 'center' },
  avatarLetter: { ...typographyScale.caption, ...fonts.bold, fontSize: 13, color: DARK.ink },
  bubble: { borderRadius: radius.lg, paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  bubbleOther: {
    backgroundColor: DARK.bubbleOther,
    borderWidth: 1,
    borderColor: DARK.bubbleBorder,
    ...shadowTokens.bar,
  },
  bubbleMine: {
    backgroundColor: colors.primary,
    borderBottomRightRadius: spacing.xs,
    ...shadowTokens.bar,
    shadowColor: colors.primary,
  },
  bubbleFailed: { opacity: 0.7 },
  senderName: { ...typographyScale.caption, ...fonts.semibold, color: colors.primaryLight, marginBottom: 2 },
  msgText: { ...typographyScale.body, color: DARK.ink },
  msgTextMine: { color: colors.white },
  metaRow: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-end', gap: spacing.xs, marginTop: 2, minHeight: 12 },
  timeText: { ...typographyScale.caption, fontSize: 10, color: DARK.muted },
  timeTextMine: { color: 'rgba(255,255,255,0.6)' },
  // Reply quote inside a bubble.
  replyQuote: { borderLeftWidth: 2, borderLeftColor: colors.primaryLight, paddingLeft: spacing.sm, marginBottom: spacing.xs, opacity: 0.95 },
  replyQuoteMine: { borderLeftColor: 'rgba(255,255,255,0.7)' },
  replyName: { ...typographyScale.caption, ...fonts.semibold, fontSize: 11, color: colors.primaryLight },
  replyNameMine: { color: 'rgba(255,255,255,0.9)' },
  replyText: { ...typographyScale.caption, color: DARK.muted },
  replyTextMine: { color: 'rgba(255,255,255,0.75)' },
  // Reply action under another member's bubble.
  replyAction: { flexDirection: 'row', alignItems: 'center', gap: 3, marginTop: 3, alignSelf: 'flex-start' },
  replyActionText: { ...typographyScale.caption, fontSize: 11, color: DARK.muted },
  // Attachments.
  imageAtt: { width: 220, height: 165, borderRadius: radius.md, backgroundColor: colors.cardBorder, marginBottom: spacing.xs },
  uploadOverlay: { position: 'absolute', top: 0, left: 0, right: 0, height: 165, borderRadius: radius.md, backgroundColor: 'rgba(26,26,46,0.35)', alignItems: 'center', justifyContent: 'center' },
  fileChip: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.xs, maxWidth: 220 },
  fileChipText: { ...typographyScale.body, fontSize: 14, flexShrink: 1 },
  // Failed-send inline retry.
  retryInline: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: 3, alignSelf: 'flex-end' },
  retryInlineText: { ...typographyScale.caption, fontSize: 11, color: colors.error },
  // Date separator (glass pill on the particle field).
  daySepWrap: { alignItems: 'center', marginVertical: spacing.md },
  dayPill: { paddingHorizontal: spacing.md, paddingVertical: 4 },
  dayPillText: { ...typographyScale.caption, ...fonts.semibold, fontSize: 11, color: DARK.ink },
  // Typing.
  typingRow: { paddingHorizontal: spacing.lg, paddingVertical: spacing.xs },
  typingText: { ...typographyScale.caption, color: DARK.muted, fontStyle: 'italic' },
  // Reply compose bar.
  replyBar: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm, backgroundColor: 'rgba(108,92,231,0.22)', borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: DARK.divider },
  replyBarBody: { flex: 1 },
  replyBarName: { ...typographyScale.caption, ...fonts.semibold, color: colors.primaryLight },
  replyBarText: { ...typographyScale.caption, color: DARK.muted },
  // Composer — glass bar (GlassSurface) so it lifts off the lavender bg.
  composerWrap: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: DARK.divider,
  },
  composer: {
    flexDirection: 'row', alignItems: 'flex-end', gap: spacing.sm,
    paddingHorizontal: spacing.lg, paddingVertical: spacing.md,
  },
  iconBtn: { width: 40, height: 40, borderRadius: radius.full, alignItems: 'center', justifyContent: 'center' },
  input: {
    flex: 1, maxHeight: 120, backgroundColor: DARK.inputBg, borderRadius: radius.lg,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    ...typographyScale.body, color: DARK.ink,
    borderWidth: 1, borderColor: DARK.bubbleBorder,
  },
  sendBtn: {
    width: 40, height: 40, borderRadius: radius.full, backgroundColor: colors.primary,
    alignItems: 'center', justifyContent: 'center',
    ...shadowTokens.bar, shadowColor: colors.primary,
  },
  sendBtnDisabled: { opacity: 0.4, shadowOpacity: 0, elevation: 0 },
  empty: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: spacing.sm, paddingTop: 80, paddingHorizontal: spacing.xl },
  emptyIconCircle: {
    width: 84, height: 84, borderRadius: radius.full, backgroundColor: 'rgba(108,92,231,0.18)',
    borderWidth: 1, borderColor: 'rgba(168,85,247,0.35)',
    alignItems: 'center', justifyContent: 'center', marginBottom: spacing.sm,
  },
  emptyText: { ...typographyScale.h3, color: DARK.ink, textAlign: 'center' },
  emptySub: { ...typographyScale.body, fontSize: 13, color: DARK.muted, textAlign: 'center' },
  // Error / forbidden state — glass card.
  errCard: { width: '100%', maxWidth: 340 },
  errCardContent: { alignItems: 'center', padding: spacing.xl },
  errIconCircle: {
    width: 64, height: 64, borderRadius: radius.full, backgroundColor: 'rgba(108,92,231,0.22)',
    alignItems: 'center', justifyContent: 'center', marginBottom: spacing.md,
  },
  errTitle: { ...typographyScale.h3, color: DARK.ink, textAlign: 'center' },
  errSub: { ...typographyScale.body, color: DARK.muted, textAlign: 'center', marginTop: spacing.xs },
  errCta: { marginTop: spacing.lg },
});
